import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DelivererPendingMap from './DelivererPendingMap';
import DelivererLoadChecklist from './DelivererLoadChecklist';
import DelivererPaidHistory from './DelivererPaidHistory';
import DelivererDebt from './DelivererDebt';
import PublicToolsMenu from '../components/public/PublicToolsMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'deliverer_token';

// Cliente axios con auth automática por token
const dax = axios.create({ baseURL: API });
dax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export default function DelivererPortal() {
  const [nipInput, setNipInput] = useState('');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('home'); // 'home' | 'pending'

  // Al montar, si hay token intenta recuperar sesión
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    (async () => {
      try {
        const { data } = await dax.get('/public/deliverer/me');
        setMe(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (nipInput.length !== 4) {
      setError('El NIP debe ser de 4 dígitos');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await dax.post('/public/deliverer/login', { nip: nipInput });
      localStorage.setItem(TOKEN_KEY, data.token);
      setMe(data.deliverer);
      setNipInput('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'NIP no reconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await dax.post('/public/deliverer/logout'); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setMe(null);
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
        <PublicToolsMenu current="/entregador" />
        <form
          onSubmit={handleLogin}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm space-y-5"
          data-testid="deliverer-login-form"
        >
          <div className="text-center">
            <div className="text-5xl mb-2">🚚</div>
            <h1 className="text-xl font-bold text-slate-800">Portal de Entregador</h1>
            <p className="text-xs text-slate-500 mt-1">Ingresa tu NIP para acceder</p>
          </div>
          <div>
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              pattern="[0-9]{4}"
              maxLength={4}
              value={nipInput}
              onChange={(e) => { setNipInput(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(''); }}
              placeholder="••••"
              className="w-full text-center text-4xl tracking-[0.5em] font-mono font-bold py-4 border-2 border-slate-200 rounded-lg focus:outline-none focus:border-red-500"
              data-testid="deliverer-nip-input"
            />
          </div>
          {error && (
            <p className="text-sm text-rose-600 text-center" data-testid="deliverer-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || nipInput.length !== 4}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-lg btn-transition"
            data-testid="deliverer-login-btn"
          >
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
          <p className="text-[11px] text-slate-400 text-center">
            ¿No sabes tu NIP? Pídeselo al administrador.
          </p>
        </form>
      </div>
    );
  }

  // Sub-vistas post-login
  if (view === 'pending') {
    return <DelivererPendingMap onBack={() => setView('home')} />;
  }
  if (view === 'load') {
    return <DelivererLoadChecklist onBack={() => setView('home')} />;
  }
  if (view === 'history') {
    return <DelivererPaidHistory onBack={() => setView('home')} />;
  }
  if (view === 'debt') {
    return <DelivererDebt onBack={() => setView('home')} />;
  }

  // Home con menú
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-red-600 text-white px-4 pb-3 shadow" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wide opacity-80">Portal Entregador</p>
            <h1 className="text-lg font-bold" data-testid="deliverer-name">👋 {me.name}</h1>
          </div>
          <button
            onClick={handleLogout}
            className="text-xs bg-red-700 hover:bg-red-800 px-3 py-1.5 rounded-full"
            data-testid="deliverer-logout-btn"
          >Salir</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <MenuCard icon="📍" title="Envíos pendientes" description="Ver mis envíos en el mapa con ruta óptima" testid="menu-pending" onClick={() => setView('pending')} />
        <MenuCard icon="📦" title="Preparar carga" description="Elegir qué envíos voy a llevar y ver la mercancía" testid="menu-load" onClick={() => setView('load')} />
        <MenuCard icon="✅" title="Historial pagado" description="Envíos ya cobrados con productos y fecha" testid="menu-history" onClick={() => setView('history')} />
        <MenuCard icon="💰" title="Mi deuda" description="Cuánto debo y a qué envío corresponde" testid="menu-debt" onClick={() => setView('debt')} />
      </div>
      <p className="text-center text-[11px] text-slate-400 pb-4">Sistema POS · Portal Entregador v1.0</p>
    </div>
  );
}

function MenuCard({ icon, title, description, testid, comingSoon, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={comingSoon}
      className="w-full bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-red-300 disabled:opacity-60 disabled:cursor-not-allowed btn-transition text-left flex items-center gap-4"
      data-testid={testid}
    >
      <div className="text-3xl">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
      {comingSoon && <span className="text-[10px] bg-amber-100 text-amber-700 font-semibold px-2 py-1 rounded-full">Próximamente</span>}
    </button>
  );
}
